import { toInputDate } from "@/utils/dateUtils";
import { useState } from "react";
import { BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MyAuctionsStatsDialog } from "@/components/my-auctions/MyAuctionsStatsDialog";
import { ListingService } from "@/services/listing/listingService";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import type { ListingStatus } from "@/types/listing";
import { resolveListingIdFromEvent } from "@/utils/resolveListingFromEvent";
import { toast } from "sonner";

interface ListingAuctionStatsButtonProps {
  listingId?: string | null;
  fallbackResolve?: {
    regNumber?: string;
    title?: string | null;
  };
  vehicleName: string;
  regNumber?: string;
  listingStatus?: ListingStatus;
  className?: string;
  size?: "sm" | "default" | "icon";
  variant?: "outline" | "ghost" | "default";
}

export function ListingAuctionStatsButton({
  listingId,
  fallbackResolve,
  vehicleName,
  regNumber,
  listingStatus = "completed",
  className,
  size = "sm",
  variant = "outline",
}: ListingAuctionStatsButtonProps) {
  const { language } = useLanguage();
  const bi = carDetailsLabels(language).biddingInfo;
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statsData, setStatsData] = useState<any>(null);
  const [sellerName, setSellerName] = useState<string | undefined>(undefined);
  const [sellerEmail, setSellerEmail] = useState<string | undefined>(undefined);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      let resolvedListingId = listingId ?? null;
      if (!resolvedListingId && fallbackResolve) {
        resolvedListingId = await resolveListingIdFromEvent({
          regNumber: fallbackResolve.regNumber ?? regNumber,
          title: fallbackResolve.title,
        });
      }

      if (!resolvedListingId) {
        toast.error(bi.statsListingNotFound ?? "Kunde inte hitta annonsen");
        return;
      }

      const [bidsResponse, listingResponse] = await Promise.all([
        ListingService.getListingBids(resolvedListingId),
        ListingService.getListing(resolvedListingId),
      ]);

      const bids = bidsResponse.data || [];
      const listing = listingResponse.listing;
      const highestBidAmount =
        bids.length > 0 ? Math.max(...bids.map((b) => b.amount || 0)) : 0;
      const sortedBids = [...bids].sort((a, b) => b.amount - a.amount);
      const bidHistory = sortedBids.map((bid, index) => ({
        id: bid.id,
        bidder: bid.bidderName || "••••••••",
        amount: `${bid.amount.toLocaleString("sv-SE")} :-`,
        timestamp: new Date(bid.createdAt).toLocaleString("sv-SE", {
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }),
        isHighest: index === 0 && bid.amount === highestBidAmount,
        ...(bid.bidderEmail ? { email: bid.bidderEmail } : {}),
      }));

      const uniqueBidderIds = new Set(bids.map((b) => b.bidderId));
      const currentPrice =
        listing.auctionDetails?.currentBid ??
        listing.auctionDetails?.startingPrice ??
        listing.directPurchaseDetails?.currentPrice ??
        0;
      const startingPrice = listing.auctionDetails?.startingPrice ?? 0;
      const listingWithViews = listing as typeof listing & {
        uniqueViews?: number;
      };

      setSellerName(listing.sellerDisplayName?.trim() || undefined);
      setSellerEmail(listing.sellerEmail?.trim() || undefined);
      setStatsData({
        totalViews: listing.views || 0,
        uniqueViewers: listingWithViews.uniqueViews ?? listing.views ?? 0,
        totalBids: bids.length,
        uniqueBidders: uniqueBidderIds.size,
        favoriteCount: bidsResponse.favoriteCount ?? 0,
        currentPrice: `${Number(currentPrice).toLocaleString("sv-SE")} :-`,
        startingPrice: `${Number(startingPrice).toLocaleString("sv-SE")} :-`,
        endDate: listing.endTime
          ? toInputDate(new Date(listing.endTime))
          : bi.completed,
        bids: bidHistory,
      });
      setOpen(true);
    } catch {
      setSellerName(undefined);
      setSellerEmail(undefined);
      setStatsData({
        totalViews: 0,
        uniqueViewers: 0,
        totalBids: 0,
        uniqueBidders: 0,
        favoriteCount: 0,
        currentPrice: "0 :-",
        startingPrice: "0 :-",
        endDate: bi.completed,
        bids: [],
      });
      setOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={handleClick}
        disabled={isLoading}
        aria-label={bi.statistics}
      >
        <BarChart3 className="w-3.5 h-3.5" />
      </Button>
      <MyAuctionsStatsDialog
        open={open}
        onOpenChange={setOpen}
        stats={
          statsData || {
            totalViews: 0,
            uniqueViewers: 0,
            totalBids: 0,
            uniqueBidders: 0,
            favoriteCount: 0,
            currentPrice: "0 :-",
            startingPrice: "0 :-",
            endDate: bi.completed,
            bids: [],
          }
        }
        vehicleName={vehicleName}
        regNumber={regNumber}
        isLoading={isLoading}
        isAdmin
        status={listingStatus}
        sellerName={sellerName}
        sellerEmail={sellerEmail}
      />
    </>
  );
}
